<template>
	<transition name="bounce" type="animation" @after-enter="updateDisplay">
		<div class="popup-holder">
			<div class="popup cycle-summary">
				<img src="@assets/img/logo-sm.svg" class="logo" />
				<h1 class="cycle-heading">{{ cycleName }} {{ completedGameCycles }}</h1>
				<div class="score-info">
					<h5 class="score-label" v-once>{{ ui.score_label }}</h5>
					<h4 class="score"><AnimatedNumber :number="display.score" /></h4>
				</div>
				<div class="penalty-info">
					<KittyKarma v-if="display.killed > 0" />
				</div>
				<div class="cycle-info">
					<div class="score-row bg-dark">
						<span class="score-group fed">
							<span class="score-type" v-once>{{ ui.cats_fed_label }}</span>
							<span class="score-value"><AnimatedNumber :number="display.fed" /></span>
						</span>
						<span class="score-group killed">
							<span class="score-type" v-once>{{ ui.cats_killed_label }}</span>
							<span class="score-value"><AnimatedNumber :number="display.killed" /></span>
						</span>
					</div>
					<div class="score-row">
						<span class="score-group hungry">
							<span class="score-type" v-once>{{ ui.cats_unfed_label }}</span>
							<span class="score-value"><AnimatedNumber :number="display.hungry" /></span>
						</span>
					</div>
				</div>
				<div class="footer">
					<Button class="dark" :action="giveUp" v-if="currentStageSettings.final_stage">
						{{ ui.quit_button_text }}
					</Button>
					<Button :action="completeReview" v-once>{{ ui.continue_button_text }}</Button>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import AnimatedNumber from '@/components/abstract/AnimatedNumber';
import KittyKarma from '@/components/board/KittyKarma';
import Button from '@/components/abstract/Button';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
	name: 'CycleSummary',
	props: ["stage"],
	components: {
		AnimatedNumber,
		KittyKarma,
		Button,
	},
	data() {
		return {
			display: {
				score: 0,
				killed: 0,
				fed: 0,
				hungry: 0,
			},
		};
	},
	computed: {
		...mapState({
			cycleName: state => state.config.general.unit_of_time,
			completedGameCycles: state => state.cycle.completedGameCycles,
			score: state => state.score.score,
			killedCount: state => state.cycle.actions.killed,
			satisfiedCount: state => state.cycle.actions.satisfied,
			unsatisfiedCount: state => state.cycle.actions.unsatisfied,
			ui: state => state.config.ui.score_summary,
		}),
		...mapGetters([
			"catsKilled",
			"currentStageSettings",
		]),
	},
	methods: {
		...mapActions([
			"updateScore",
			"completeReview",
			"reviewHidden",
			"sendEvent",
			"stopClock",
		]),
		updateDisplay() {
			this.setDisplayValues({
				score: this.score,
				killed: this.killedCount,
				fed: this.satisfiedCount,
				hungry: this.unsatisfiedCount,
			});
		},
		setDisplayValues(values) {
			this.display = values;
		},
		giveUp() {
			this.sendEvent({
				ec: "Game",
				ea: "Give Up",
				el: `${this.currentStageSettings.name} - Day ${this.completedGameCycles}`,
			});

			this.$store.dispatch("loadView", {
				view: "EndScreen",
			});
			this.$store.commit("removeAllTimers");
			this.stopClock();
		},
	},
	created() {
		this.setDisplayValues({
			score: this.score,
			killed: 0,
			fed: 0,
			hungry: 0,
		});

		// super hack that I want to get rid of.  Double nextTick ensures that all cat
		// deaths have completed before tallying the score. Cat deaths occur inside of
		// a nextTick (to ensure any last instant clicks register) and therefore
		// we need 2 nextTicks here to make sure we don't tally the score until the tick after that.
		this.$nextTick(() => {
			this.$nextTick(() => {
				this.updateScore();
			});
		});
	},
};
</script>

<style lang="scss" scoped>
	.cycle-summary {
		display: flex;
		width: 90%;
		max-width: 500px;
		max-height: 500px;
		flex-direction: column;

		font-size: 18px;
		text-transform: uppercase;
		text-align: center;
		color: #fff;

		@include mobile{
			min-width: 60%;
			max-width: 400px;
		}
	}

	.logo {
		width: 33.33%;
		position: absolute;
		left: -10%;
		top: -10%;

		@include mobile {
			width: 30%;
			left: 50%;
			transform: translateX(-50%);
		}
	}

	.cycle-heading {
		font-size: 50px;
		padding-top: .5em;
		text-transform: none;

		@include mobile {
			font-size: 44px;
		}
	}

	.cycle-info {
		position: relative;
		z-index: 10;
		box-shadow: 0 0 20px 1px rgba(0, 0, 0, 0.25);
	}


	.score-row {
		display: flex;
		justify-content: center;
		padding: .5em 0;
		background-color: $brand-color-primary-light;

		&.bg-dark {
			background-color: $brand-color-primary-dark;
		}
	}

	.score-label {
		margin: .5em 0 .5em;
		line-height: 1;
		color: $brand-color-primary-extra-light;

		@include mobile {
			margin: 0.5em 0 .5em;
		}
	}

	.score {
		font-size: 70px;
		color: $brand-color-alt;
		font-weight: 700;
		letter-spacing: .025em;
		line-height: 1;
		margin: 0 0 0;

		@include mobile {
			font-size: 60px;
			margin: 0;
		}
	}

	.penalty-info {
		display: flex;
		min-height: 1.25em;
		justify-content: center;
		align-items: center;
		flex-shrink: 0;

		transition: all $ui-transition-duration;
	}

	.score-group {
		display: flex;
		align-items: center;
		flex-shrink: 1;

		.score-type {
			color: $brand-color-primary-extra-light;
			font-weight: 700;
			letter-spacing: .07em;
		}

		.score-value {
			margin-left: .25em;
			font-size: 150%;

			@include mobile {
				font-size: 125%;
			}
		}

		& + & {
			margin-left: 2em;
		}
	}

	.footer {
		width: 100%;
		display: flex;
		justify-content: center;
		bottom: 0;
		border-radius: 0 0 20px 20px;
		background-color: $brand-color-primary;

		.button {
			flex-basis: 175px;
			flex-shrink: 0;
			flex-grow: 0;
			font-size: 26px;
			padding: .25em 0;
			text-align: center;
			margin: 0 .5em;
			transform: translateY(50%);

			@include mobile {
				font-size: 20px;
			}
		}
	}

</style>



// WEBPACK FOOTER //
// src/components/board/CycleSummary.vue